// =================================================================
// TemplateManager.js - Modal CRUD pour gérer les templates utilisateur
// Session 34 — Templates toolbar éditables
// =================================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Pencil, Trash2, Check, ChevronDown } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const TemplateManager = ({ isOpen, onClose, onTemplatesChanged, initialEdit }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'Personnel' });

  // Pré-remplir le formulaire si on arrive depuis un bouton "éditer" du dropdown
  useEffect(() => {
    if (isOpen && initialEdit) {
      setForm({ title: initialEdit.title, content: initialEdit.content, category: initialEdit.category || 'Personnel' });
      setEditingId(initialEdit.id || null); // null = créer une copie perso (template système)
      setShowForm(true);
    }
  }, [isOpen, initialEdit]);

  const token = localStorage.getItem('oracle_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadTemplates = async () => {
    try {
      const res = await axios.get('/api/templates', { headers });
      setTemplates(res.data?.templates || []);
    } catch (err) {
      console.error('Erreur chargement templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOpen) loadTemplates(); }, [isOpen]);

  const resetForm = () => {
    setForm({ title: '', content: '', category: 'Personnel' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.warning('Titre et contenu requis');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`/api/templates/${editingId}`, form, { headers });
        toast.success('Raccourci modifié');
      } else {
        await axios.post('/api/templates', form, { headers });
        toast.success('Raccourci créé');
      }
      resetForm();
      await loadTemplates();
      onTemplatesChanged && onTemplatesChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur sauvegarde');
    }
  };

  const handleEdit = (t) => {
    setForm({ title: t.title, content: t.content, category: t.category });
    setEditingId(t.id);
    setShowForm(true);
  };

  const handleDelete = async (t) => {
    const confirmed = await confirmModal(`Supprimer "${t.title}" ?`, {
      confirmText: 'Supprimer',
      danger: true
    });
    if (!confirmed) return;
    try {
      await axios.delete(`/api/templates/${t.id}`, { headers });
      toast.success('Raccourci supprimé');
      await loadTemplates();
      onTemplatesChanged && onTemplatesChanged();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  // Grouper par catégorie
  const grouped = {};
  templates.forEach(t => {
    if (!grouped[t.category]) grouped[t.category] = [];
    grouped[t.category].push(t);
  });

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--card-border)',
        borderRadius: '16px', width: '460px', maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 16px 64px rgba(0,0,0,0.4)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid var(--card-border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text)' }}>
              Mes raccourcis
            </h3>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary)', padding: '4px',
            }}>
              <X size={18} />
            </button>
          </div>
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
            Créez des prompts réutilisables pour gagner du temps. Vos raccourcis personnels s'ajoutent en haut de la liste.
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>
              Chargement...
            </div>
          ) : templates.length === 0 && !showForm ? (
            <div style={{ padding: '16px 0' }}>
              <div style={{
                padding: '20px', borderRadius: '12px',
                background: 'var(--input-bg)', border: '1px dashed var(--card-border)',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>&#9997;</div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text)', marginBottom: '6px' }}>
                  Aucun raccourci personnel
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '16px' }}>
                  Un raccourci est un prompt pré-écrit que vous pouvez insérer en un clic dans le chat.
                  <br />Par exemple : "Fais un diagnostic complet de ce projet" ou "Rédige un compte-rendu".
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button onClick={() => {
                    setForm({ title: '', content: '', category: 'Personnel' });
                    setEditingId(null);
                    setShowForm(true);
                  }}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: 'none',
                      background: '#1D4ED8', color: 'white', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                    <Plus size={14} /> Créer mon premier raccourci
                  </button>
                  <button onClick={() => {
                    setForm({
                      title: 'Diagnostic projet',
                      content: 'Fais un diagnostic complet de ce projet : forces, faiblesses, risques et opportunités. Propose un plan d\'action priorisé.',
                      category: 'Analyse',
                    });
                    setEditingId(null);
                    setShowForm(true);
                  }}
                    style={{
                      padding: '8px 16px', borderRadius: '8px',
                      border: '1px solid var(--card-border)', background: 'var(--card-bg)',
                      color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer',
                    }}>
                    Voir un exemple
                  </button>
                </div>
              </div>
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category} style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '10px', fontWeight: 600, color: '#06b6d4',
                  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
                }}>
                  {category}
                </div>
                {items.map(t => (
                  <div key={t.id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 12px', borderRadius: '8px', marginBottom: '4px',
                    background: 'var(--input-bg)', border: '1px solid transparent',
                    transition: 'border-color 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--card-border)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)', marginBottom: '2px' }}>
                        {t.title}
                      </div>
                      <div style={{
                        fontSize: '11px', color: 'var(--text-secondary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {t.content.substring(0, 80)}{t.content.length > 80 ? '...' : ''}
                      </div>
                    </div>
                    <button onClick={() => handleEdit(t)} title="Modifier" style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-secondary)', padding: '4px', flexShrink: 0,
                    }}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(t)} title="Supprimer" style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#ef4444', padding: '4px', flexShrink: 0,
                    }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}

          {/* Formulaire ajout/édition */}
          {showForm && (
            <div style={{
              padding: '16px', background: 'var(--input-bg)', borderRadius: '12px',
              border: '1px solid var(--card-border)', marginTop: '8px',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
                {editingId ? 'Modifier le raccourci' : 'Nouveau raccourci'}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                Le titre s'affiche dans la toolbar, le contenu est le prompt inséré au clic.
              </div>
              <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Titre (affiché dans le menu)</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Ex: Diagnostic projet"
                maxLength={200}
                autoFocus
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: '8px',
                  border: '1px solid var(--card-border)', background: 'var(--card-bg)',
                  color: 'var(--text)', fontSize: '13px', marginBottom: '8px',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Catégorie (pour regrouper)</label>
              <input
                type="text"
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                placeholder="Ex: Analyse, Rédaction, Stratégie..."
                maxLength={100}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: '8px',
                  border: '1px solid var(--card-border)', background: 'var(--card-bg)',
                  color: 'var(--text)', fontSize: '13px', marginBottom: '8px',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              <label style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Prompt (le texte inséré dans le chat au clic)</label>
              <textarea
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Ex: Fais un diagnostic complet de ce projet : forces, faiblesses, risques et opportunités."
                maxLength={5000}
                rows={4}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: '8px',
                  border: '1px solid var(--card-border)', background: 'var(--card-bg)',
                  color: 'var(--text)', fontSize: '13px', marginBottom: '12px', resize: 'vertical',
                  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={resetForm} style={{
                  padding: '6px 16px', borderRadius: '6px', border: '1px solid var(--card-border)',
                  background: 'var(--card-bg)', color: 'var(--text-secondary)',
                  fontSize: '12px', cursor: 'pointer',
                }}>
                  Annuler
                </button>
                <button onClick={handleSave} style={{
                  padding: '6px 16px', borderRadius: '6px', border: 'none',
                  background: '#1D4ED8', color: 'white',
                  fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                }}>
                  {editingId ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showForm && (
          <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--card-border)' }}>
            <button onClick={() => { setEditingId(null); setForm({ title: '', content: '', category: 'Personnel' }); setShowForm(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                justifyContent: 'center', padding: '10px',
                background: 'rgba(29,78,216,0.08)', border: '1px solid rgba(29,78,216,0.2)',
                borderRadius: '8px', cursor: 'pointer', color: '#3b82f6',
                fontSize: '13px', fontWeight: 500, transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(29,78,216,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(29,78,216,0.08)'}
            >
              <Plus size={14} /> Ajouter un raccourci
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateManager;
